import { ReportTargetType } from '../enums';

export const LEGAL_COMPLIANCE_SIGNATURE_MAX = 200;
export const LEGAL_COMPLIANCE_URL_MAX = 2000;
export const LEGAL_COMPLIANCE_DESCRIPTION_MAX = 4000;
export const LEGAL_COMPLIANCE_CLAIMANT_NAME_MAX = 200;
export const LEGAL_COMPLIANCE_EMAIL_MAX = 320;
export const LEGAL_COMPLIANCE_ADDRESS_MAX = 500;
export const LEGAL_COMPLIANCE_PHONE_MAX = 40;
export const LEGAL_COMPLIANCE_COUNTER_STATEMENT_MAX = 4000;
export const LEGAL_COMPLIANCE_ADMIN_NOTES_MAX = 2000;

export type DmcaTakedownInput = {
  claimantName: string;
  claimantEmail: string;
  claimantAddress?: string | null;
  claimantPhone?: string | null;
  infringingUrl: string;
  copyrightedWorkDescription: string;
  statementGoodFaith: boolean;
  statementAccuracy: boolean;
  signature: string;
};

export type DmcaCounterNoticeInput = {
  claimantName: string;
  claimantEmail: string;
  claimantAddress?: string | null;
  claimantPhone?: string | null;
  counterStatement: string;
  counterConsentJurisdiction: boolean;
  counterConsentService: boolean;
  signature: string;
};

export type ParsedInfringingTarget = {
  targetType: ReportTargetType | null;
  targetId: string | null;
  communitySlug: string | null;
};

export function normalizeLegalComplianceText(
  value: string | null | undefined,
  max: number,
): string {
  return (value ?? '').trim().slice(0, max);
}

export function validateDmcaTakedownInput(
  input: DmcaTakedownInput,
):
  | { ok: true; normalized: Required<Pick<DmcaTakedownInput, 'claimantName' | 'claimantEmail' | 'infringingUrl' | 'copyrightedWorkDescription' | 'signature'>> & DmcaTakedownInput }
  | { ok: false; message: string } {
  const claimantName = normalizeLegalComplianceText(input.claimantName, LEGAL_COMPLIANCE_CLAIMANT_NAME_MAX);
  const claimantEmail = normalizeLegalComplianceText(input.claimantEmail, LEGAL_COMPLIANCE_EMAIL_MAX);
  const infringingUrl = normalizeLegalComplianceText(input.infringingUrl, LEGAL_COMPLIANCE_URL_MAX);
  const copyrightedWorkDescription = normalizeLegalComplianceText(
    input.copyrightedWorkDescription,
    LEGAL_COMPLIANCE_DESCRIPTION_MAX,
  );
  const signature = normalizeLegalComplianceText(input.signature, LEGAL_COMPLIANCE_SIGNATURE_MAX);

  if (!claimantName) return { ok: false, message: 'Claimant name is required' };
  if (!claimantEmail || !claimantEmail.includes('@')) {
    return { ok: false, message: 'Valid claimant email is required' };
  }
  if (!infringingUrl) return { ok: false, message: 'Infringing URL is required' };
  if (!copyrightedWorkDescription) {
    return { ok: false, message: 'Description of copyrighted work is required' };
  }
  if (!input.statementGoodFaith || !input.statementAccuracy) {
    return { ok: false, message: 'Both statutory statements must be confirmed' };
  }
  if (!signature) return { ok: false, message: 'Electronic signature is required' };

  return {
    ok: true,
    normalized: {
      ...input,
      claimantName,
      claimantEmail,
      claimantAddress: normalizeLegalComplianceText(input.claimantAddress, LEGAL_COMPLIANCE_ADDRESS_MAX) || null,
      claimantPhone: normalizeLegalComplianceText(input.claimantPhone, LEGAL_COMPLIANCE_PHONE_MAX) || null,
      infringingUrl,
      copyrightedWorkDescription,
      signature,
    },
  };
}

export function validateDmcaCounterNoticeInput(
  input: DmcaCounterNoticeInput,
):
  | { ok: true; normalized: DmcaCounterNoticeInput }
  | { ok: false; message: string } {
  const claimantName = normalizeLegalComplianceText(input.claimantName, LEGAL_COMPLIANCE_CLAIMANT_NAME_MAX);
  const claimantEmail = normalizeLegalComplianceText(input.claimantEmail, LEGAL_COMPLIANCE_EMAIL_MAX);
  const counterStatement = normalizeLegalComplianceText(
    input.counterStatement,
    LEGAL_COMPLIANCE_COUNTER_STATEMENT_MAX,
  );
  const signature = normalizeLegalComplianceText(input.signature, LEGAL_COMPLIANCE_SIGNATURE_MAX);

  if (!claimantName) return { ok: false, message: 'Name is required' };
  if (!claimantEmail || !claimantEmail.includes('@')) {
    return { ok: false, message: 'Valid email is required' };
  }
  if (!counterStatement) return { ok: false, message: 'Counter-notice statement is required' };
  if (!input.counterConsentJurisdiction || !input.counterConsentService) {
    return { ok: false, message: 'Both counter-notice consents must be confirmed' };
  }
  if (!signature) return { ok: false, message: 'Electronic signature is required' };

  return {
    ok: true,
    normalized: {
      claimantName,
      claimantEmail,
      claimantAddress: normalizeLegalComplianceText(input.claimantAddress, LEGAL_COMPLIANCE_ADDRESS_MAX) || null,
      claimantPhone: normalizeLegalComplianceText(input.claimantPhone, LEGAL_COMPLIANCE_PHONE_MAX) || null,
      counterStatement,
      counterConsentJurisdiction: true,
      counterConsentService: true,
      signature,
    },
  };
}

export function parseInfringingUrl(url: string): ParsedInfringingTarget {
  const trimmed = url.trim();
  let path = trimmed;
  try {
    path = new URL(trimmed).pathname;
  } catch {
    if (!trimmed.startsWith('/')) path = `/${trimmed}`;
  }

  const postMatch =
    path.match(/\/circles\/[^/]+\/posts\/([^/?#]+)/i) ??
    path.match(/\/posts\/([^/?#]+)/i);
  if (postMatch?.[1]) {
    const slugMatch = path.match(/\/circles\/([^/]+)\/posts\//i);
    return {
      targetType: ReportTargetType.POST,
      targetId: postMatch[1],
      communitySlug: slugMatch?.[1] ?? null,
    };
  }

  const commentMatch = path.match(/\/comments\/([^/?#]+)/i);
  if (commentMatch?.[1]) {
    return {
      targetType: ReportTargetType.COMMENT,
      targetId: commentMatch[1],
      communitySlug: null,
    };
  }

  const articleMatch = path.match(/\/a\/([^/?#]+)/i);
  if (articleMatch?.[1]) {
    return {
      targetType: ReportTargetType.ARTICLE,
      targetId: articleMatch[1],
      communitySlug: null,
    };
  }

  const courseMatch = path.match(/\/courses\/([^/?#]+)/i);
  if (courseMatch?.[1]) {
    return {
      targetType: ReportTargetType.COURSE,
      targetId: courseMatch[1],
      communitySlug: null,
    };
  }

  const communityMatch = path.match(/\/circles\/([^/?#]+)/i);
  if (communityMatch?.[1]) {
    return {
      targetType: null,
      targetId: null,
      communitySlug: communityMatch[1],
    };
  }

  return { targetType: null, targetId: null, communitySlug: null };
}

export function canAdminResolveLegalRequest(status: string): boolean {
  return status === 'received' || status === 'reviewing' || status === 'counter_received';
}

export function isLegalRequestCounterNoticeEligible(parentStatus: string): boolean {
  return parentStatus === 'action_taken';
}
